import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Separator } from '../ui/separator';
import { 
  CheckCircle, 
  Edit, 
  MapPin, 
  DollarSign, 
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  Image as ImageIcon,
  Building,
  Bell,
  Shield
} from 'lucide-react';
import { availableServices } from '../../data/services';

export const ReviewSubmitStep = ({ formData, updateFormData, onSubmit, isSubmitting, validationErrors = {}, onEditStep }) => {
  const handleTermsChange = (checked) => {
    updateFormData({ agreeToTerms: checked });
  };

  const getServiceCategoryLabel = (serviceId) => {
    const service = availableServices.find(s => s.id == serviceId);
    return service ? service.name : `Service ${serviceId}`;
  };

  const getUrgencyLabel = (value) => {
    const urgencies = {
      'asap': 'ASAP (Urgent)',
      'this-week': 'This Week',
      'this-month': 'This Month', 
      'flexible': 'Flexible Timeline'
    };
    return urgencies[value] || value;
  };

  const getBudgetRangeLabel = (value) => {
    const ranges = {
      'under_500': 'Under $500',
      '500_1000': '$500 - $1,000',
      '1000_5000': '$1,000 - $5,000',
      '5000_10000': '$5,000 - $10,000',
      '10000_25000': '$10,000 - $25,000',
      'over_25000': 'Over $25,000',
      // Legacy values from old inquiries — still display sensibly
      'under_1000': 'Under $1,000',
      'between_1000_5000': '$1,000 - $5,000',
      'between_5000_10000': '$5,000 - $10,000',
      'between_10000_25000': '$10,000 - $25,000',
      'not_sure': 'Budget flexible'
    };
    return ranges[value] || value;
  };

  const getContactMethodLabel = (value) => {
    const methods = {
      'email': 'Email Only',
      'phone': 'Phone Only',
      'both': 'Both Email & Phone'
    };
    return methods[value] || value;
  };

  return (
    <div className="space-y-8 mb-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Review Your Project
        </h3>
        <p className="text-gray-600">
          Please review all details before posting your project to receive bids
        </p>
      </div>

      {/* Project Overview */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">
                {getServiceCategoryLabel(formData.service_id) || 'Your Project'}
              </h4>
              <div className="flex items-center space-x-4 text-sm">
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  {getServiceCategoryLabel(formData.service_id)}
                </Badge>
                <div className="flex items-center space-x-1 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{getUrgencyLabel(formData.urgency)}</span>
                </div>
              </div>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          
          {formData.details && (
            <p className="text-gray-700 bg-white p-4 rounded-lg border border-emerald-200">
              {formData.details}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Review Sections */}
      <div className="grid gap-6">
        {/* Project Details */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-gray-600" />
                <h4 className="text-lg font-semibold text-gray-900">Project Details</h4>
              </div>
              <button
                onClick={() => onEditStep && onEditStep(2)}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>

            <div className="space-y-4">
              {formData.photos.length > 0 ? (
                <div>
                  <Label className="text-sm font-medium text-gray-500 mb-2 block">
                    Photos ({formData.photos.length})
                  </Label>
                  <div className="flex items-center space-x-2">
                    <ImageIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {formData.photos.length} photo{formData.photos.length !== 1 ? 's' : ''} uploaded
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No photos uploaded</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Location & Budget */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-gray-600" />
                <h4 className="text-lg font-semibold text-gray-900">Location & Budget</h4>
              </div>
              <button
                onClick={() => onEditStep && onEditStep(3)}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Location</Label>
                <p className="text-gray-900">
                  {formData.fullAddress || formData.zipcode}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Budget Range</Label>
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <p className="text-gray-900">{getBudgetRangeLabel(formData.budget)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-gray-600" />
                <h4 className="text-lg font-semibold text-gray-900">Contact Information</h4>
              </div>
              <button
                onClick={() => onEditStep && onEditStep(4)}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Name</Label>
                  <p className="text-gray-900">{formData.contact_first_name} {formData.contact_last_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Contact Method</Label>
                  <p className="text-gray-900">{getContactMethodLabel(formData.contactMethod)}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-900">{formData.contact_email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-900">{formData.contact_phone}</span>
                </div>
              </div>

              {/* Notification Preferences */}
              <div>
                <Label className="text-sm font-medium text-gray-500 mb-2 block">Notifications</Label>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-1">
                    <Bell className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">
                      {[
                        formData.notifications.bidReceived && 'New Bids',
                        formData.notifications.messages && 'Messages',
                        formData.notifications.updates && 'Updates'
                      ].filter(Boolean).join(', ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Terms and Conditions */}
      <Card className="border-2 border-gray-200">
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-start space-x-3">
              <Shield className="w-6 h-6 text-emerald-600 mt-1" />
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Terms & Conditions
                </h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <p>
                    By posting this project, you agree to FixDirectly's terms of service and understand that:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Your project will be visible to verified service providers in your area</li>
                    <li>You'll receive competitive bids from qualified professionals</li>
                    <li>You have no obligation to accept any bid</li>
                    <li>Your contact information will only be shared with providers you choose</li>
                    <li>You can message providers and view their profiles before deciding</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Email Updates Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
              <Checkbox
                id="emailUpdates"
                checked={formData.email_updates ?? true}
                onCheckedChange={(checked) => updateFormData({ email_updates: checked })}
                className="mt-1"
              />
              <div className="flex-1">
                <label htmlFor="emailUpdates" className="text-sm text-gray-900 cursor-pointer leading-relaxed">
                  <span className="font-medium">Email me when providers bid on this project</span>
                  <p className="text-gray-500 text-xs mt-0.5">
                    You'll receive an email with the provider's name, price, and start date each time a new bid comes in.
                  </p>
                </label>
              </div>
            </div>

            {/* SMS Updates Checkbox  */}
            <div className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
              <Checkbox
                id="smsUpdates"
                checked={formData.sms_updates ?? false}
                onCheckedChange={(checked) => updateFormData({ sms_updates: checked })}
                className="mt-1"
              />
              <div className="flex-1">
                <label htmlFor="smsUpdates" className="text-sm text-gray-900 cursor-pointer leading-relaxed">
                  <span className="font-medium">I agree to receive SMS job alerts and updates</span>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Message &amp; data rates may apply. Reply STOP to opt out.
                  </p>
                </label>
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Checkbox
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onCheckedChange={handleTermsChange}
                className={`mt-1 ${validationErrors.agreeToTerms ? 'border-red-500' : ''}`}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-red-600 font-bold" aria-hidden="true">*</span>
                  <span className="text-xs font-bold uppercase tracking-wide text-red-600">Required</span>
                </div>
                <label
                  htmlFor="agreeToTerms"
                  className="text-sm font-semibold text-gray-900 cursor-pointer leading-relaxed"
                >
                  I agree to the{' '}
                  <a href="#" className="text-emerald-600 hover:text-emerald-700 underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-emerald-600 hover:text-emerald-700 underline">
                    Privacy Policy
                  </a>
                  . I understand that posting this project will make it visible to service providers
                  and I may receive bids and messages.
                </label>
              </div>
            </div>
            {validationErrors.agreeToTerms && (
              <p className="text-sm text-red-600 font-medium mt-2">{validationErrors.agreeToTerms}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Final CTA */}
      <div className="text-center bg-gradient-to-r from-emerald-50 to-teal-50 p-8 rounded-2xl border border-emerald-200">
        <div className="max-w-2xl mx-auto">
          <h4 className="text-xl font-bold text-gray-900 mb-2">
            Ready to Get Competitive Bids?
          </h4>
          <p className="text-gray-600 mb-6">
            Once you post your project, qualified service providers will start submitting bids. 
            You'll typically receive 3-7 bids within 24-48 hours.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span>Free to post</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span>No obligation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span>Compare bids easily</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};